import React, { useState, useId, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import Popover from '../Popover';
import ListItem from '../ListItem';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
}

export interface SelectProps {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  label: string;
  id?: string;
  disabled?: boolean;
  className?: string;
}

const Select: React.FC<SelectProps> = ({
  value,
  options,
  onChange,
  label,
  id,
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const autoId = useId();
  const buttonId = id ?? autoId;
  const listboxId = `${buttonId}-listbox`;

  const selectedOption = options.find(opt => opt.value === value);

  const handleSelect = useCallback((optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  }, [onChange]);

  const trigger = (
    <>
      <label htmlFor={buttonId} className="label block mb-2">
        {label}
      </label>
      <button
        type="button"
        id={buttonId}
        onClick={() => !disabled && setIsOpen(prev => !prev)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        className={[
          'w-full h-12 px-4 text-left bg-white border rounded-[1rem]',
          'flex items-center justify-between gap-2 transition-all duration-150',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
          isOpen
            ? 'border-primary-500 ring-2 ring-primary-500 ring-offset-0'
            : 'border-gray-300 hover:border-primary-400',
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        ].join(' ')}
      >
        <span className="font-medium text-gray-800 truncate">
          {selectedOption?.label ?? value}
        </span>
        <ChevronDown
          size={18}
          aria-hidden="true"
          className={`flex-shrink-0 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
    </>
  );

  return (
    <div className={className}>
      <Popover isOpen={isOpen} onClose={() => setIsOpen(false)} anchor={trigger}>
        <ul
          id={listboxId}
          role="listbox"
          aria-label={label}
          className="bg-white border border-gray-200 rounded-[1rem] shadow-lg overflow-hidden max-h-[300px] overflow-y-auto p-2"
        >
          {options.map(option => (
            <li key={option.value}>
              <ListItem
                label={option.label}
                description={option.description}
                isSelected={option.value === value}
                onClick={() => handleSelect(option.value)}
              />
            </li>
          ))}
        </ul>
      </Popover>
    </div>
  );
};

export default Select;
